/**
 * Knowledge Articles React Query Hooks
 * 
 * Provides React Query hooks for knowledge article operations with caching,
 * optimistic updates, and automatic refetching.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { 
  knowledgeArticleService, 
  type CreateKnowledgeArticleInput, 
  type UpdateKnowledgeArticleInput 
} from '../services/KnowledgeArticleService';
import type { KnowledgeArticle } from '../repositories/KnowledgeArticleRepository';

/**
 * Query keys for knowledge article queries
 */
export const knowledgeArticleKeys = {
  all: ['knowledgeArticles'] as const,
  lists: () => [...knowledgeArticleKeys.all, 'list'] as const,
  list: (filters?: any) => [...knowledgeArticleKeys.lists(), filters] as const,
  details: () => [...knowledgeArticleKeys.all, 'detail'] as const,
  detail: (id: string) => [...knowledgeArticleKeys.details(), id] as const,
  byCategory: (category: string) => [...knowledgeArticleKeys.lists(), 'category', category] as const,
  byTag: (tag: string) => [...knowledgeArticleKeys.lists(), 'tag', tag] as const,
  search: (query: string) => [...knowledgeArticleKeys.lists(), 'search', query] as const,
  mostViewed: (companyId: string) => [...knowledgeArticleKeys.lists(), companyId, 'mostViewed'] as const,
  categories: (companyId: string) => [...knowledgeArticleKeys.all, companyId, 'categories'] as const,
};

/**
 * Hook to fetch all knowledge articles
 */
export function useKnowledgeArticles(
  companyId: string,
  options?: UseQueryOptions<KnowledgeArticle[], Error>
) {
  return useQuery<KnowledgeArticle[], Error>({
    queryKey: knowledgeArticleKeys.list({ companyId }),
    queryFn: () => knowledgeArticleService.getArticles(companyId),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Hook to fetch knowledge articles by category
 */
export function useKnowledgeArticlesByCategory(
  companyId: string,
  category: string,
  options?: UseQueryOptions<KnowledgeArticle[], Error>
) {
  return useQuery<KnowledgeArticle[], Error>({
    queryKey: knowledgeArticleKeys.byCategory(category),
    queryFn: () => knowledgeArticleService.getArticlesByCategory(companyId, category),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId && !!category,
    ...options,
  });
}

/**
 * Hook to fetch knowledge articles by tag
 */
export function useKnowledgeArticlesByTag(
  companyId: string,
  tag: string,
  options?: UseQueryOptions<KnowledgeArticle[], Error>
) {
  return useQuery<KnowledgeArticle[], Error>({
    queryKey: knowledgeArticleKeys.byTag(tag),
    queryFn: () => knowledgeArticleService.getArticlesByTag(companyId, tag),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId && !!tag,
    ...options,
  });
}

/**
 * Hook to search knowledge articles
 */
export function useSearchKnowledgeArticles(
  companyId: string,
  query: string,
  options?: UseQueryOptions<KnowledgeArticle[], Error>
) {
  return useQuery<KnowledgeArticle[], Error>({
    queryKey: knowledgeArticleKeys.search(query),
    queryFn: () => knowledgeArticleService.searchArticles(companyId, query),
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
    enabled: !!companyId && query.length > 0,
    ...options,
  });
}

/**
 * Hook to fetch most viewed articles
 */
export function useMostViewedArticles(
  companyId: string,
  limit: number = 10,
  options?: UseQueryOptions<KnowledgeArticle[], Error>
) {
  return useQuery<KnowledgeArticle[], Error>({
    queryKey: knowledgeArticleKeys.mostViewed(companyId),
    queryFn: () => knowledgeArticleService.getMostViewed(companyId, limit),
    staleTime: 10 * 60 * 1000,
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Hook to fetch available categories
 */
export function useKnowledgeCategories(
  companyId: string,
  options?: UseQueryOptions<string[], Error>
) {
  return useQuery<string[], Error>({
    queryKey: knowledgeArticleKeys.categories(companyId),
    queryFn: () => knowledgeArticleService.getCategories(companyId),
    staleTime: 10 * 60 * 1000,
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Hook to fetch a single knowledge article
 */
export function useKnowledgeArticle(
  id: string,
  options?: UseQueryOptions<KnowledgeArticle | null, Error>
) {
  return useQuery<KnowledgeArticle | null, Error>({
    queryKey: knowledgeArticleKeys.detail(id),
    queryFn: () => knowledgeArticleService.getArticle(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to create a new knowledge article
 */
export function useCreateKnowledgeArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateKnowledgeArticleInput) => 
      knowledgeArticleService.createArticle(input),
    onSuccess: (newArticle) => {
      queryClient.setQueryData(knowledgeArticleKeys.detail(newArticle.id), newArticle);
      queryClient.invalidateQueries({ queryKey: knowledgeArticleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: knowledgeArticleKeys.categories(newArticle.company_id) });
    },
  });
}

/**
 * Hook to update a knowledge article
 */
export function useUpdateKnowledgeArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKnowledgeArticleInput }) =>
      knowledgeArticleService.updateArticle(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: knowledgeArticleKeys.detail(id) });
      
      const previousArticle = queryClient.getQueryData<KnowledgeArticle>(
        knowledgeArticleKeys.detail(id)
      );
      
      if (previousArticle) {
        queryClient.setQueryData<KnowledgeArticle>(knowledgeArticleKeys.detail(id), {
          ...previousArticle,
          ...data,
        });
      }
      
      return { previousArticle };
    },
    onError: (err, { id }, context) => {
      if (context?.previousArticle) {
        queryClient.setQueryData(knowledgeArticleKeys.detail(id), context.previousArticle);
      }
    },
    onSuccess: (updatedArticle, { id }) => {
      queryClient.setQueryData(knowledgeArticleKeys.detail(id), updatedArticle);
      queryClient.invalidateQueries({ queryKey: knowledgeArticleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: knowledgeArticleKeys.categories(updatedArticle.company_id) });
    },
  });
}

/**
 * Hook to increment article view count
 */
export function useIncrementArticleViews() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => knowledgeArticleService.recordView(id),
    onSuccess: (updatedArticle, id) => {
      queryClient.setQueryData(knowledgeArticleKeys.detail(id), updatedArticle);
      queryClient.invalidateQueries({ 
        queryKey: knowledgeArticleKeys.mostViewed(updatedArticle.company_id) 
      });
    },
  });
}

/**
 * Hook to delete a knowledge article
 */
export function useDeleteKnowledgeArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => knowledgeArticleService.deleteArticle(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: knowledgeArticleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: knowledgeArticleKeys.lists() });
    },
  });
}
